/**
 * Build-time configuration.
 *
 * Only transport and environment settings live here. Business values — prices,
 * limits, statistics, payment methods — are fetched from the server, never
 * compiled into the app.
 */

import { Platform } from 'react-native';

import { buildFlags } from '@mfa/build-flags';

/** Android emulators reach the host machine on 10.0.2.2, not localhost. */
const devHost = Platform.select({
  android: 'http://10.0.2.2:8000',
  default: 'http://localhost:8000',
});

export interface AppConfigValues {
  apiBaseUrl: string;
  environment: 'development' | 'staging' | 'production';
  /** Seconds a cached query stays fresh before a background refetch. */
  staleTimeSeconds: number;
  enableAnalytics: boolean;
  /**
   * Serve every request from bundled JSON instead of the network.
   *
   * Used for the shareable demo build: the app is fully navigable with no
   * server, and a donation created in the demo behaves like a real one.
   */
  demoMode: boolean;
  /**
   * Where the web app is served from, with a trailing slash: `/` on a host of
   * its own, `/app/` when it shares a domain with the company site. Always
   * `/` on a phone.
   */
  basePath: string;
}

const fromEnv = (key: string): string | undefined =>
  typeof process !== 'undefined' ? process.env?.[key] : undefined;

/**
 * Values injected into the page at serve time by the web build.
 *
 * Reading them at runtime means one set of built assets can be pointed at any
 * environment. A value compiled into the bundle cannot be corrected without a
 * rebuild, and a stale bundle talking to the wrong API is a silent failure.
 */
interface RuntimeConfig {
  apiBaseUrl?: string;
  environment?: AppConfigValues['environment'];
  demoMode?: boolean;
  basePath?: string;
}

const runtime: RuntimeConfig =
  (globalThis as { __MFA_CONFIG__?: RuntimeConfig }).__MFA_CONFIG__ ?? {};

// Page-injected first (web), then the environment (web dev server), then the
// host compiled into the bundle (native), and only then the local default.
const apiHost =
  runtime.apiBaseUrl || fromEnv('MFA_API_URL') || buildFlags.apiBaseUrl || devHost;

export const config: AppConfigValues = {
  apiBaseUrl: `${apiHost.replace(/\/$/, '')}/api/v1`,
  environment:
    runtime.environment ??
    (fromEnv('MFA_ENV') as AppConfigValues['environment']) ??
    'development',
  staleTimeSeconds: 60,
  enableAnalytics: fromEnv('MFA_ANALYTICS') === 'true',
  // Page-injected first (web), then the environment (web dev server),
  // then the flag compiled into the bundle (native).
  demoMode: runtime.demoMode ?? (fromEnv('MFA_DEMO') === 'true' || buildFlags.demoMode),
  basePath: runtime.basePath || '/',
};
